import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../AxiosInstance';
import '../styles/v2.css';

/* ── tiny helpers ── */
function formatProductDisplay(p) {
  let s = p.product_name;
  if (p.variety) s += ` - ${p.variety}`;
  if (p.brand)   s += ` (${p.brand})`;
  if (p.size)    s += ` [${p.size}]`;
  return s;
}

function today() {
  return new Date().toISOString().split('T')[0];
}

/* ── field wrapper ── */
function Field({ label, hint, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 13, fontWeight: 700, color: '#475569' }}>{label}</label>
      {children}
      {hint && <p style={{ fontSize: 11, color: '#94a3b8', margin: 0 }}>{hint}</p>}
    </div>
  );
}

/* ── read-only display field ── */
function ReadonlyField({ label, value, accent }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 13, fontWeight: 700, color: '#475569' }}>{label}</label>
      <div style={{
        padding: '11px 14px', borderRadius: 10,
        background: accent ? '#f0fdf4' : '#f8fafc',
        border: `1.5px solid ${accent ? '#a7f3d0' : '#e2e8f0'}`,
        fontSize: 14, fontWeight: 600,
        color: accent ? '#065f46' : '#64748b',
      }}>
        {value}
      </div>
    </div>
  );
}

/* ── Toast ── */
function Toast({ message, severity, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 5000);
    return () => clearTimeout(t);
  }, [onClose]);

  const colors = {
    success: { bg: '#f0fdf4', border: '#a7f3d0', text: '#065f46', icon: '#10b981' },
    error:   { bg: '#fef2f2', border: '#fecaca', text: '#b91c1c', icon: '#ef4444' },
    warning: { bg: '#fffbeb', border: '#fde68a', text: '#92400e', icon: '#f59e0b' },
  };
  const c = colors[severity] || colors.success;

  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
      zIndex: 9999, maxWidth: 420, width: 'calc(100% - 48px)',
      background: c.bg, border: `1px solid ${c.border}`,
      borderRadius: 14, padding: '14px 16px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
      display: 'flex', alignItems: 'flex-start', gap: 12,
    }}>
      <div style={{ width: 20, height: 20, borderRadius: '50%', background: c.icon, flexShrink: 0, marginTop: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {severity === 'success'
          ? <svg style={{ width: 12, height: 12, color: 'white' }} fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
          : <svg style={{ width: 12, height: 12, color: 'white' }} fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
        }
      </div>
      <p style={{ flex: 1, fontSize: 13, fontWeight: 500, color: c.text, margin: 0, lineHeight: 1.5 }}>{message}</p>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.text, opacity: 0.5, padding: 0, flexShrink: 0 }}>
        <svg style={{ width: 16, height: 16 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
      </button>
    </div>
  );
}

export default function AddSaleV2() {
  const navigate = useNavigate();

  /* form state */
  const [productName, setProductName]       = useState('');
  const [productDetails, setProductDetails] = useState('');
  const [currentProduct, setCurrentProduct] = useState(null);
  const [isProductSelected, setIsProductSelected] = useState(false);

  const [unitTypes, setUnitTypes]           = useState([]);
  const [selectedUnitId, setSelectedUnitId] = useState('');

  const [retailPrice, setRetailPrice]       = useState('');
  const [quantity, setQuantity]             = useState('');
  const [discount, setDiscount]             = useState('0');
  const [saleDate, setSaleDate]             = useState(today());
  const [dateWarning, setDateWarning]       = useState('');

  const [currentStockInfo, setCurrentStockInfo] = useState(null);
  const [showInventoryLink, setShowInventoryLink] = useState(false);
  const [inventoryLinkData, setInventoryLinkData] = useState(null);

  /* search */
  const [searchResults, setSearchResults]   = useState([]);
  const [isOnline, setIsOnline]             = useState(navigator.onLine);
  const [searchFocused, setSearchFocused]   = useState(false);

  /* toast */
  const [toast, setToast] = useState(null);

  const justSelectedRef  = useRef(false);
  const blurTimeoutRef   = useRef(null);
  const searchInputRef   = useRef(null);

  /* online / offline */
  useEffect(() => {
    const up   = () => setIsOnline(true);
    const down = () => setIsOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    };
  }, []);

  /* product search */
  useEffect(() => {
    if (productName.length > 2 && isOnline && !justSelectedRef.current) {
      axiosInstance.get(`/products/search?q=${productName}`)
        .then(res => {
          const unique = Object.values(
            res.data.reduce((acc, p) => {
              const key = `${p.product_name}-${p.variety||''}-${p.brand||''}-${p.size||''}`;
              if (!acc[key]) acc[key] = p;
              return acc;
            }, {})
          );
          setSearchResults(unique);
        })
        .catch(() => setSearchResults([]));
    } else {
      setSearchResults([]);
    }
    if (justSelectedRef.current) justSelectedRef.current = false;
  }, [productName, isOnline]);

  /* computed */
  const selectedUnit = unitTypes.find(u => String(u.unit_id) === String(selectedUnitId));
  const total = (() => {
    const p = parseFloat(retailPrice) || 0;
    const q = parseFloat(quantity) || 0;
    const d = parseFloat(discount) || 0;
    return Math.max(0, p * q - d).toFixed(2);
  })();

  /* handlers */
  const handleProductNameChange = (val) => {
    setProductName(val);
    setProductDetails('');
    setIsProductSelected(false);
    setSelectedUnitId('');
    setRetailPrice('');
    setCurrentProduct(null);
    setCurrentStockInfo(null);
    setUnitTypes([]);
    setShowInventoryLink(false);
  };

  const handleSelectProduct = (product) => {
    if (blurTimeoutRef.current) { clearTimeout(blurTimeoutRef.current); blurTimeoutRef.current = null; }
    justSelectedRef.current = true;
    const display = formatProductDisplay(product);
    setProductName(display);
    setProductDetails(display);
    setCurrentProduct(product);
    setIsProductSelected(true);
    setSelectedUnitId('');
    setRetailPrice('');
    setSearchResults([]);
    setShowInventoryLink(false);

    axiosInstance.get(`/units/product/${product.product_id}`)
      .then(res => {
        const selling = res.data.filter(u => u.unit_category === 'selling');
        setUnitTypes(selling.map(u => ({ unit_type: u.unit_type, unit_id: u.unit_id, unit_category: u.unit_category })));
      })
      .catch(() => {});
  };

  const handleUnitChange = async (unitId) => {
    setSelectedUnitId(unitId);
    setCurrentStockInfo(null);
    if (!unitId || !currentProduct) return;
    try {
      const res = await axiosInstance.get(`/sales/price-suggestions/${currentProduct.product_id}/${unitId}`);
      if (res.data.has_price_history) setRetailPrice(res.data.suggested_retail_price.toString());
      else setRetailPrice('');
    } catch { setRetailPrice(''); }
    try {
      const res = await axiosInstance.get(`/inventories/product/${currentProduct.product_id}/unit/${unitId}`);
      setCurrentStockInfo(res.data);
    } catch { setCurrentStockInfo(null); }
  };

  const getStockDisplay = () => {
    if (!selectedUnitId) return 'Select a unit to view stock';
    if (!currentStockInfo) return 'Not available';
    const val = Number(currentStockInfo.current_stock ?? 0);
    const formatted = Number.isFinite(val) ? val.toFixed(2) : '-';
    const unit = currentStockInfo.unit_type ? ` ${currentStockInfo.unit_type}` : '';
    return `${formatted}${unit}`;
  };

  const checkFutureDate = (date) => {
    setDateWarning(date > today() ? 'Note: You are entering a future date for this sale.' : '');
  };

  const generateInventoryLink = () => {
    if (!inventoryLinkData) return '';
    return `/new/dashboard/inventories/add?product_id=${inventoryLinkData.productId}&product_name=${encodeURIComponent(inventoryLinkData.productName)}&variety=${encodeURIComponent(inventoryLinkData.variety || '')}`;
  };

  const resetForm = () => {
    setProductName(''); setProductDetails(''); setCurrentProduct(null);
    setIsProductSelected(false); setUnitTypes([]); setSelectedUnitId('');
    setRetailPrice(''); setQuantity(''); setDiscount('0');
    setSaleDate(today()); setDateWarning(''); setCurrentStockInfo(null);
    setShowInventoryLink(false);
    searchInputRef.current?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowInventoryLink(false);

    if (!selectedUnit) {
      setToast({ message: 'Please select a unit type.', severity: 'error' });
      return;
    }

    /* parse productDetails back into components */
    let pName, variety, brand, size = '';
    let withoutSize = productDetails;
    if (productDetails.includes('[') && productDetails.includes(']')) {
      const m = productDetails.match(/\[([^\]]+)\]$/);
      size = m ? m[1] : '';
      withoutSize = productDetails.replace(/\s*\[[^\]]+\]$/, '');
    }
    if (withoutSize.includes('(') && withoutSize.includes(')')) {
      const m = withoutSize.match(/\(([^)]+)\)$/);
      brand = m ? m[1] : '';
      const withoutBrand = withoutSize.replace(/\s*\([^)]+\)$/, '');
      if (withoutBrand.includes(' - ')) { [pName, variety] = withoutBrand.split(' - '); }
      else { pName = withoutBrand; variety = ''; }
    } else if (withoutSize.includes(' - ')) {
      [pName, variety] = withoutSize.split(' - '); brand = '';
    } else {
      pName = withoutSize; variety = ''; brand = '';
    }

    try {
      await axiosInstance.post('/sales', {
        product_name: pName,
        variety: variety || '',
        brand: brand || '',
        size: size || '',
        retail_price: retailPrice,
        quantity,
        sale_date: saleDate,
        unit_id: selectedUnitId,
        unit_category: selectedUnit.unit_category,
        discount,
        trans_id: `TXN-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`,
      });
      setToast({ message: 'Sale recorded successfully!', severity: 'success' });
      resetForm();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || '';
      if (msg.includes('Inventory not found') || msg.includes('add the item to inventory') || msg.includes('not found for product')) {
        setInventoryLinkData({ productId: currentProduct?.product_id, productName: pName, variety: variety || '', brand: brand || '' });
        setShowInventoryLink(true);
        setToast({ message: `${msg} Set the stock first, then retry.`, severity: 'error' });
      } else {
        setToast({ message: 'Sale failed. Check that you have enough stock in inventory.', severity: 'error' });
      }
    }
  };

  const stockValue = currentStockInfo ? Number(currentStockInfo.current_stock ?? 0) : null;
  const stockLow = stockValue !== null && stockValue <= 5;

  return (
    <div style={{ maxWidth: 680, margin: '0 auto' }}>

      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <button
          onClick={() => navigate('/new/dashboard')}
          style={{ width: 36, height: 36, borderRadius: 10, border: '1.5px solid #e2e8f0', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', flexShrink: 0 }}
        >
          <svg style={{ width: 16, height: 16 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
        </button>
        <div>
          <h1 style={{ color: '#0f172a', fontSize: 20, fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>Add Sale</h1>
          <p style={{ color: '#94a3b8', fontSize: 13, margin: '2px 0 0' }}>Add a new sale transaction</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* ── Product search card ── */}
          <div className="v2-card" style={{ padding: 24 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>Product</p>

            <Field label="Search Product" hint="Type at least 3 characters to search">
              <div style={{ position: 'relative' }}>
                <svg style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: '#94a3b8', pointerEvents: 'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                </svg>
                <input
                  ref={searchInputRef}
                  className="v2-input"
                  style={{ paddingLeft: 40 }}
                  type="text"
                  placeholder="Search by product name…"
                  value={productName}
                  onChange={e => handleProductNameChange(e.target.value)}
                  onFocus={() => { setSearchFocused(true); if (blurTimeoutRef.current) { clearTimeout(blurTimeoutRef.current); blurTimeoutRef.current = null; } }}
                  onBlur={() => { blurTimeoutRef.current = setTimeout(() => { setSearchResults([]); setSearchFocused(false); }, 150); }}
                  autoComplete="off"
                  data-testid="sale-form-product-search"
                />
                {isProductSelected && (
                  <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 20, height: 20, borderRadius: '50%', background: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg style={{ width: 12, height: 12, color: 'white' }} fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                  </div>
                )}
              </div>

              {/* Search dropdown */}
              {searchResults.length > 0 && (
                <div style={{ marginTop: 4, border: '1.5px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', background: 'white', boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}>
                  {searchResults.map((product, i) => (
                    <button
                      key={i}
                      type="button"
                      data-testid="sale-product-result"
                      onMouseDown={() => handleSelectProduct(product)}
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                        width: '100%', padding: '12px 16px', background: 'none', border: 'none',
                        borderBottom: i < searchResults.length - 1 ? '1px solid #f1f5f9' : 'none',
                        cursor: 'pointer', textAlign: 'left', transition: 'background 0.1s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}
                    >
                      <span style={{ fontSize: 13.5, fontWeight: 600, color: '#1e293b' }}>{product.product_name}</span>
                      <span style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 2 }}>
                        {[product.brand && `Brand: ${product.brand}`, product.variety && `Variety: ${product.variety}`, product.size && `Size: ${product.size}`].filter(Boolean).join(' · ') || 'No details'}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </Field>
          </div>

          {/* ── Sale details card (shown after product selected) ── */}
          {isProductSelected && (
            <div className="v2-card" style={{ padding: 24 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>Sale Details</p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                {/* Unit */}
                <Field label="Unit Type">
                  <div style={{ position: 'relative' }}>
                    <select
                      className="v2-input"
                      style={{ paddingRight: 36, appearance: 'none', cursor: 'pointer' }}
                      value={selectedUnitId}
                      onChange={e => handleUnitChange(e.target.value)}
                      required
                      data-testid="sale-form-unit-select"
                    >
                      <option value="">Select a selling unit…</option>
                      {unitTypes.map(u => (
                        <option key={u.unit_id} value={u.unit_id} data-testid="sale-unit-option">{u.unit_type}</option>
                      ))}
                    </select>
                    <svg style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: '#94a3b8', pointerEvents: 'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                    </svg>
                  </div>
                  {unitTypes.length === 0 && (
                    <p style={{ fontSize: 11, color: '#f59e0b', margin: '4px 0 0', fontWeight: 500 }}>No selling units found for this product.</p>
                  )}
                </Field>

                {/* Current stock */}
                {selectedUnitId && (
                  <ReadonlyField
                    label="Current Stock"
                    value={getStockDisplay()}
                    accent={!stockLow}
                  />
                )}
                {stockLow && selectedUnitId && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, background: '#fffbeb', border: '1px solid #fde68a' }}>
                    <svg style={{ width: 16, height: 16, color: '#f59e0b', flexShrink: 0 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                    </svg>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#92400e' }}>Low stock — consider reordering soon.</span>
                  </div>
                )}

                {/* Price + Quantity side by side on desktop */}
                <div className="v2-sale-price-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label={selectedUnit ? `Price per ${selectedUnit.unit_type} (K)` : 'Retail Price (K)'}>
                    <input
                      className="v2-input"
                      type="number"
                      placeholder="0.00"
                      value={retailPrice}
                      onChange={e => setRetailPrice(e.target.value)}
                      min="0"
                      step="0.01"
                      required
                      data-testid="sale-form-retail-price"
                    />
                  </Field>

                  <Field label="Quantity">
                    <input
                      className="v2-input"
                      type="number"
                      placeholder="0"
                      value={quantity}
                      onChange={e => setQuantity(e.target.value)}
                      min="0"
                      step="0.01"
                      required
                      data-testid="sale-form-quantity"
                    />
                  </Field>
                </div>

                {/* Discount */}
                <Field label="Discount (K)" hint="Optional — enter 0 for no discount">
                  <input
                    className="v2-input"
                    type="number"
                    placeholder="0.00"
                    value={discount}
                    onChange={e => setDiscount(Math.max(0, e.target.value || 0))}
                    min="0"
                    step="0.01"
                    data-testid="sale-form-discount"
                  />
                </Field>

                {/* Sale date */}
                <Field label="Sale Date">
                  <input
                    className="v2-input"
                    type="date"
                    value={saleDate}
                    onChange={e => { setSaleDate(e.target.value); checkFutureDate(e.target.value); }}
                    required
                    data-testid="sale-form-date"
                  />
                  {dateWarning && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                      <svg style={{ width: 14, height: 14, color: '#f59e0b', flexShrink: 0 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9.303 3.376c.866 1.5-.217 3.374-1.948 3.374H4.645c-1.73 0-2.813-1.874-1.948-3.374L10.05 3.378c.866-1.5 3.032-1.5 3.898 0l5.354 9.248zM12 15.75h.007v.008H12v-.008z" />
                      </svg>
                      <span style={{ fontSize: 11, color: '#92400e', fontWeight: 500 }}>{dateWarning}</span>
                    </div>
                  )}
                </Field>
              </div>
            </div>
          )}

          {/* ── Order summary ── */}
          {isProductSelected && selectedUnitId && retailPrice && quantity && (
            <div style={{ background: 'linear-gradient(135deg,#f0fdf4,#ecfdf5)', borderRadius: 16, padding: 20, border: '1.5px solid #a7f3d0' }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#065f46', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Order Summary</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { label: `${quantity} × K ${parseFloat(retailPrice || 0).toFixed(2)}`, value: `K ${(parseFloat(retailPrice || 0) * parseFloat(quantity || 0)).toFixed(2)}` },
                  ...(parseFloat(discount) > 0 ? [{ label: 'Discount', value: `− K ${parseFloat(discount).toFixed(2)}`, red: true }] : []),
                ].map(row => (
                  <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 13, color: '#475569' }}>{row.label}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: row.red ? '#ef4444' : '#475569' }}>{row.value}</span>
                  </div>
                ))}
                <div style={{ height: 1, background: '#a7f3d0', margin: '4px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#065f46' }}>Total</span>
                  <span style={{ fontSize: 18, fontWeight: 900, color: '#065f46', letterSpacing: '-0.02em' }}>K {total}</span>
                </div>
              </div>
            </div>
          )}

          {/* ── Inventory missing link ── */}
          {showInventoryLink && inventoryLinkData && (
            <div style={{ padding: '14px 16px', borderRadius: 12, background: '#fef2f2', border: '1px solid #fecaca' }}>
              <p style={{ fontSize: 13, color: '#b91c1c', fontWeight: 500, margin: '0 0 8px' }}>No inventory record found for this product.</p>
              <a
                href={generateInventoryLink()}
                style={{ fontSize: 13, color: '#b91c1c', fontWeight: 700, textDecoration: 'underline' }}
              >
                Set initial stock for {inventoryLinkData.productName}{inventoryLinkData.variety && ` - ${inventoryLinkData.variety}`} →
              </a>
            </div>
          )}

          {/* ── Submit ── */}
          {isProductSelected && (
            <button
              type="submit"
              className="v2-btn-submit"
              data-testid="sale-form-save"
              style={{ fontSize: 15 }}
            >
              Add Sale
            </button>
          )}
        </div>
      </form>

      {/* Toast */}
      {toast && <Toast message={toast.message} severity={toast.severity} onClose={() => setToast(null)} />}
    </div>
  );
}
